import { useState, useRef, useCallback } from "react";
import { LocationAutocomplete } from "../ui";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  locationToStop,
  syncStopsIntoFormData,
} from "../../utils/stopsMigration";
import { validateTrip } from "../../utils/tripValidation";
import { API_URL } from "../../config";
import { routePost } from "../../services/api";

const dayTripSubtitle = (enriched, origin, enrichedStops) => {
  if (!enriched || enriched.kind !== "day_trip") return null;
  const cluster = enriched.cluster;
  if (cluster && cluster.size > 1 && !cluster.isFirst) {
    const prevMemberId = cluster.memberIds[cluster.index - 1];
    const prevStop = enrichedStops?.find(s => s.id === prevMemberId);
    return `Combined day visit with ${prevStop?.name || "the previous stop"}`;
  }
  const before = enriched.baseBefore;
  const after = enriched.baseAfter;
  if (!before || !after) return "Day visit";
  const originName = origin?.name || "your origin";
  if (before.kind === "stay" && after.kind === "stay") {
    if (before.name && before.name === after.name) {
      return `Day trip from ${before.name}, return same evening`;
    }
    return `Day visit en route ${before.name} → ${after.name}`;
  }
  if (before.kind === "origin" && after.kind === "stay") {
    return `Day visit from ${before.name || originName}, sleep at ${after.name}`;
  }
  if (before.kind === "stay" && after.kind === "origin") {
    return `Day visit before returning to ${after.name || originName}`;
  }
  if (before.kind === "origin" && after.kind === "origin") {
    return `Single-day visit from ${originName}`;
  }
  return "Day visit";
};


const GripIcon = () => (
  <svg viewBox="0 0 16 16" fill="currentColor" className="w-3.5 h-3.5">
    <circle cx="5" cy="3" r="1.3" />
    <circle cx="11" cy="3" r="1.3" />
    <circle cx="5" cy="8" r="1.3" />
    <circle cx="11" cy="8" r="1.3" />
    <circle cx="5" cy="13" r="1.3" />
    <circle cx="11" cy="13" r="1.3" />
  </svg>
);

const MinusIcon = () => (
  <svg
    className="w-3 h-3"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2.5}
  >
    <path strokeLinecap="round" d="M5 12h14" />
  </svg>
);

const PlusIcon = () => (
  <svg
    className="w-3 h-3"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2.5}
  >
    <path strokeLinecap="round" d="M12 5v14M5 12h14" />
  </svg>
);

const CheckIcon = () => (
  <svg
    className="w-4 h-4"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2.5}
      d="M5 13l4 4L19 7"
    />
  </svg>
);

const PencilIcon = () => (
  <svg
    className="w-3.5 h-3.5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M15.232 5.232l3.536 3.536M9 13l6.586-6.586a2 2 0 012.828 2.828L11.828 15.828a2 2 0 01-1.414.586H9v-1.414a2 2 0 01.586-1.414z"
    />
  </svg>
);

const XIcon = () => (
  <svg
    className="w-3.5 h-3.5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2.5}
      d="M6 18L18 6M6 6l12 12"
    />
  </svg>
);

const RouteIcon = () => (
  <svg
    className="w-3.5 h-3.5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
    />
  </svg>
);

const ConfirmCheckIcon = () => (
  <svg
    className="w-3.5 h-3.5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    strokeWidth={2.5}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);

const ConfirmXIcon = () => (
  <svg
    className="w-3.5 h-3.5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    strokeWidth={2.5}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M6 18L18 6M6 6l12 12"
    />
  </svg>
);

const buildConsecutiveGroups = (stops) => {
  const groups = [];
  stops.forEach((stop) => {
    const country = stop.country || "Other";
    const last = groups[groups.length - 1];
    if (last && last.country === country) {
      last.items.push(stop);
    } else {
      groups.push({
        key: `${country}_${groups.length}`,
        country,
        countryCode: stop.countryCode || null,
        items: [stop],
      });
    }
  });
  return groups;
};

const SortableStopRow = ({
  stop,
  enriched,
  enrichedStops,
  origin,
  isLast,
  onNightsChange,
  isEditing,
  onEditStart,
  onEditCommit,
  onEditCancel,
  onMount,
}) => {
  const [searchVal, setSearchVal] = useState("");
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: `stop::${stop.id}`,
    disabled: isEditing,
  });

  const combinedRef = useCallback(
    (el) => {
      setNodeRef(el);
      onMount?.(stop.id, el);
    },
    [setNodeRef, stop.id, onMount],
  );

  const handleSelect = (location) => {
    if (location) {
      onEditCommit(stop.id, location);
      setSearchVal("");
    }
  };

  const handleCancel = () => {
    setSearchVal("");
    onEditCancel();
  };

  const nights = parseInt(stop.nights, 10) || 0;
  const isDayTrip = nights === 0;
  const subtitle = isDayTrip ? dayTripSubtitle(enriched, origin, enrichedStops) : null;

  return (
    <div
      ref={combinedRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`flex items-center px-4 border-t border-gray-50 transition-colors duration-150 ${isEditing ? "py-2.5 bg-teal-50/40" : "py-3 hover:bg-gray-50/60"
        } ${isDragging ? "opacity-30" : ""}`}
    >
      {!isEditing && (
        <button
          {...attributes}
          {...listeners}
          className="mr-2 p-1 rounded text-gray-300 hover:text-teal-400 cursor-grab active:cursor-grabbing transition-colors duration-150 shrink-0 touch-none"
          aria-label={`Reorder ${stop.name}`}
        >
          <GripIcon />
        </button>
      )}

      <div
        className={`flex flex-col items-center self-stretch w-4 shrink-0 mr-3 ${isEditing ? "ml-7" : ""}`}
      >
        <div className="w-px bg-gray-200 flex-1" />
        <div className="w-2 h-px bg-gray-300" />
        {!isLast && <div className="w-px bg-gray-200 flex-1" />}
      </div>

      <div className="flex items-center justify-between flex-1 gap-2 min-w-0">
        {isEditing ? (
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <div className="flex-1 min-w-0 [&_input]:py-1.5 [&_input]:text-sm [&_input]:rounded-lg [&_input]:pl-3">
              <LocationAutocomplete
                value={searchVal}
                onChange={setSearchVal}
                onSelect={handleSelect}
                placeholder={`Replace ${stop.name}...`}
                filterType="city"
              />
            </div>
            <button
              type="button"
              onClick={handleCancel}
              aria-label="Cancel edit"
              className="w-7 h-7 rounded-full border border-gray-200 flex items-center justify-center text-gray-400 hover:border-red-300 hover:text-red-400 hover:bg-red-50 transition-all duration-150 cursor-pointer shrink-0"
            >
              <XIcon />
            </button>
          </div>
        ) : (
          <>
            <div className="min-w-0 flex items-center gap-2 flex-1">
              <div className="min-w-0">
                <div className="flex items-center gap-2 min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">
                    {stop.name}
                  </p>
                  {isDayTrip && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-teal-50 border border-teal-200 text-[10px] font-semibold uppercase tracking-wide text-teal-700 shrink-0">
                      Day Trip
                    </span>
                  )}
                </div>
                {subtitle ? (
                  <p className="text-xs text-teal-600 mt-0.5 truncate">
                    {subtitle}
                  </p>
                ) : (
                  stop.type && stop.type !== "country" && (
                    <p className="text-xs text-gray-400 capitalize">
                      {stop.type}
                    </p>
                  )
                )}
              </div>
              <button
                type="button"
                onClick={onEditStart}
                aria-label={`Edit ${stop.name}`}
                className="p-1 rounded text-gray-300 hover:text-teal-500 hover:bg-teal-50 transition-all duration-150 cursor-pointer shrink-0 opacity-0 group-hover:opacity-100"
              >
                <PencilIcon />
              </button>
            </div>

            <div className="flex items-center gap-1.5 shrink-0">
              <button
                type="button"
                onClick={() => onNightsChange(stop.id, -1)}
                disabled={nights <= 0}
                aria-label={`Decrease nights for ${stop.name}`}
                className="w-7 h-7 rounded-full border border-gray-200 flex items-center justify-center text-gray-500 hover:border-teal-400 hover:text-teal-600 hover:bg-teal-50 disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-150 cursor-pointer"
              >
                <MinusIcon />
              </button>
              <span className="text-sm font-semibold text-gray-900 w-14 text-center tabular-nums">
                {isDayTrip ? (
                  <span className="text-teal-600">0n</span>
                ) : (
                  <>
                    {nights}
                    <span className="text-gray-400 font-normal text-xs">n</span>
                  </>
                )}
              </span>
              <button
                type="button"
                onClick={() => onNightsChange(stop.id, 1)}
                aria-label={`Increase nights for ${stop.name}`}
                className="w-7 h-7 rounded-full border border-gray-200 flex items-center justify-center text-gray-500 hover:border-teal-400 hover:text-teal-600 hover:bg-teal-50 disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-150 cursor-pointer"
              >
                <PlusIcon />
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const SortableCountrySection = ({
  groupKey,
  country,
  countryCode,
  items,
  groupIdx,
  enrichedById,
  enrichedStops,
  origin,
  onNightsChange,
  editingStopId,
  onEditStart,
  onEditCommit,
  onEditCancel,
  onStopMount,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: `country::${groupKey}`,
  });

  const stopIds = items.map((s) => `stop::${s.id}`);
  const groupNights = items.reduce(
    (s, stop) => s + (parseInt(stop.nights, 10) || 0),
    0,
  );

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`${groupIdx > 0 ? "border-t border-gray-100" : ""} ${isDragging ? "opacity-30" : ""}`}
    >
      <div className="flex items-center justify-between px-3 py-2.5 bg-gray-50/80">
        <div className="flex items-center gap-2">
          <button
            {...attributes}
            {...listeners}
            className="p-1 rounded text-gray-300 hover:text-teal-400 cursor-grab active:cursor-grabbing transition-colors duration-150 shrink-0 touch-none"
            aria-label={`Reorder ${country}`}
          >
            <GripIcon />
          </button>
          {countryCode && (
            <img
              src={`https://flagcdn.com/w20/${countryCode.toLowerCase()}.png`}
              srcSet={`https://flagcdn.com/w40/${countryCode.toLowerCase()}.png 2x`}
              alt={country}
              className="w-5 h-3.5 rounded-sm object-cover shadow-sm shrink-0"
            />
          )}
          <span className="text-xs font-semibold text-gray-600 uppercase tracking-wider">
            {country}
          </span>
          <span className="text-xs text-gray-400 font-normal">
            {items.length} {items.length === 1 ? "place" : "places"}
          </span>
        </div>
        <span className="text-xs font-medium text-teal-600 tabular-nums">
          {groupNights}n total
        </span>
      </div>

      <SortableContext items={stopIds} strategy={verticalListSortingStrategy}>
        <div className="group">
          {items.map((stop, i) => (
            <SortableStopRow
              key={stop.id}
              stop={stop}
              enriched={enrichedById?.get(stop.id) || null}
              enrichedStops={enrichedStops}
              origin={origin}
              isLast={i === items.length - 1}
              onNightsChange={onNightsChange}
              isEditing={editingStopId === stop.id}
              onEditStart={() => onEditStart(stop.id)}
              onEditCommit={onEditCommit}
              onEditCancel={onEditCancel}
              onMount={onStopMount}
            />
          ))}
        </div>
      </SortableContext>
    </div>
  );
};

const StopOverlayCard = ({ stop }) => (
  <div className="flex items-center gap-3 px-4 py-3 bg-white rounded-xl border border-teal-300 shadow-2xl ring-2 ring-teal-100 scale-[1.02]">
    <span className="text-gray-400">
      <GripIcon />
    </span>
    <div className="flex items-center justify-between flex-1 gap-3">
      <div>
        <p className="text-sm font-medium text-gray-800">{stop.name}</p>
        {stop.type && stop.type !== "country" && (
          <p className="text-xs text-gray-400 capitalize">{stop.type}</p>
        )}
      </div>
      <span className="text-sm font-semibold text-gray-900 tabular-nums">
        {parseInt(stop.nights, 10) || 0}
        <span className="text-gray-400 font-normal text-xs">n</span>
      </span>
    </div>
  </div>
);

const CountryOverlayCard = ({
  country,
  countryCode,
  itemCount,
  groupNights,
}) => (
  <div className="flex items-center justify-between px-4 py-3 bg-white rounded-xl border border-teal-300 shadow-2xl ring-2 ring-teal-100 scale-[1.02]">
    <div className="flex items-center gap-2">
      <span className="text-gray-400">
        <GripIcon />
      </span>
      {countryCode && (
        <img
          src={`https://flagcdn.com/w20/${countryCode.toLowerCase()}.png`}
          srcSet={`https://flagcdn.com/w40/${countryCode.toLowerCase()}.png 2x`}
          alt={country}
          className="w-5 h-3.5 rounded-sm object-cover shadow-sm"
        />
      )}
      <span className="text-xs font-semibold text-gray-600 uppercase tracking-wider">
        {country}
      </span>
      <span className="text-xs text-gray-400">
        {itemCount} {itemCount === 1 ? "place" : "places"}
      </span>
    </div>
    <span className="text-xs font-medium text-teal-600 tabular-nums">
      {groupNights}n total
    </span>
  </div>
);

const runFlipAnimation = (stops, stopEls, snapshots) => {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      stops.forEach((s) => {
        const el = stopEls.current.get(s.id);
        const oldTop = snapshots.get(s.id);
        if (!el || oldTop === undefined) return;
        const newTop = el.getBoundingClientRect().top;
        const deltaY = oldTop - newTop;
        if (Math.abs(deltaY) < 2) return;
        const anim = el.animate(
          [
            { transform: `translateY(${deltaY}px)`, opacity: 0.65 },
            { transform: "translateY(0px)", opacity: 1 },
          ],
          {
            duration: 480,
            easing: "cubic-bezier(0.34, 1.56, 0.64, 1)",
            fill: "both",
          },
        );
        anim.onfinish = () => anim.cancel();
      });
    });
  });
};

// When the backend Optimize Route returns destinations (no stop IDs), we re-attach
// IDs by name matching against the existing stops to preserve identity and per-stop
// state where possible.
const reconcileOptimizedStops = (prevStops, optimizedDestinations) => {
  const remaining = [...prevStops];
  const next = [];
  optimizedDestinations.forEach((dest) => {
    const idx = remaining.findIndex(
      (s) =>
        typeof s?.name === "string" &&
        typeof dest?.name === "string" &&
        s.name.toLowerCase() === dest.name.toLowerCase(),
    );
    if (idx >= 0) {
      const matched = remaining.splice(idx, 1)[0];
      next.push({
        ...matched,
        ...dest,
        id: matched.id,
        nights: parseInt(matched.nights, 10) || 0,
      });
    } else {
      next.push(locationToStop(dest, parseInt(dest?.suggestedNights, 10) || 0));
    }
  });
  return next;
};

const CustomStaySection = ({
  formData,
  setFormData,
  numberOfNights,
  totalAssignedNights,
  nightsLeft,
  onPendingChange,
}) => {
  const [activeId, setActiveId] = useState(null);
  const [editingStopId, setEditingStopId] = useState(null);
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [pendingOptimized, setPendingOptimized] = useState(false);
  const [isAccepted, setIsAccepted] = useState(false);
  const preOptimizeStopsRef = useRef(null);

  const setPending = (val) => {
    setPendingOptimized(val);
    onPendingChange?.(val);
  };

  const stopEls = useRef(new Map());
  const handleStopMount = useCallback((id, el) => {
    if (el) stopEls.current.set(id, el);
    else stopEls.current.delete(id);
  }, []);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const stops = Array.isArray(formData.stops) ? formData.stops : [];
  const origin = formData.startingLocationData
    ? {
      name: formData.startingLocationData.name || formData.startingLocation || null,
      lat: formData.startingLocationData.lat ?? formData.startingLocationData.latitude ?? null,
      lon: formData.startingLocationData.lon ?? formData.startingLocationData.longitude ?? null,
    }
    : formData.startingLocation
      ? { name: formData.startingLocation, lat: null, lon: null }
      : null;
  const validation = validateTrip({ origin, stops, totalNights: numberOfNights });
  const enrichedStops = validation.enriched;
  const enrichedById = new Map(enrichedStops.map((s) => [s.id, s]));
  const dayTripCount = enrichedStops.filter((s) => s.kind === "day_trip").length;
  const totalDays = numberOfNights > 0 ? numberOfNights + 1 : 0;
  const bannerErrors = validation.errors.filter((e) => e.id !== "V1");
  const groups = buildConsecutiveGroups(stops);
  const countryIds = groups.map((g) => `country::${g.key}`);

  const optimizeRoute = async () => {
    if (isOptimizing || pendingOptimized) return;
    setIsOptimizing(true);

    try {
      const res = await routePost("/api/route/optimize", {
        destinations: formData.destinations,
        startingLocation: formData.startingLocationData || null,
        travelToDest: formData.travelToDest,
        totalNights: numberOfNights,
        startDate: formData.startDate,
        themes: formData.themes || [],
        food: formData.food || [],
      });
      if (!res.ok) throw new Error("Optimization failed");
      const data = await res.json();

      if (Array.isArray(data.destinations) && data.destinations.length > 0) {
        preOptimizeStopsRef.current = [...stops];

        const snapshots = new Map();
        stops.forEach((s) => {
          const el = stopEls.current.get(s.id);
          if (el) snapshots.set(s.id, el.getBoundingClientRect().top);
        });

        const isManual = (() => {
          if (numberOfNights <= 0 || stops.length === 0) return false;
          const base = Math.floor(numberOfNights / stops.length);
          let currentTotal = 0;
          let manual = false;
          stops.forEach((s) => {
            const n = parseInt(s.nights, 10) || 0;
            currentTotal += n;
            if (n !== base && n !== base + 1) manual = true;
          });
          if (currentTotal !== numberOfNights) return false;
          return manual;
        })();

        let reconciled = reconcileOptimizedStops(stops, data.destinations);
        const suggestedSum = data.destinations.reduce(
          (s, d) => s + (parseInt(d.suggestedNights, 10) || 0),
          0,
        );
        if (!isManual && suggestedSum > 0 && suggestedSum === numberOfNights) {
          reconciled = reconciled.map((stop, i) => ({
            ...stop,
            nights: parseInt(data.destinations[i]?.suggestedNights, 10) || 0,
          }));
        }

        setFormData((prev) => syncStopsIntoFormData(prev, reconciled));

        setIsAccepted(false);
        setPending(true);

        runFlipAnimation(reconciled, stopEls, snapshots);
      }
    } catch {
    } finally {
      setIsOptimizing(false);
    }
  };

  const handleAcceptOptimize = () => {
    setPending(false);
    setIsAccepted(true);
    preOptimizeStopsRef.current = null;
  };

  const handleRevertOptimize = () => {
    const original = preOptimizeStopsRef.current;
    if (!original) {
      setPending(false);
      return;
    }

    const snapshots = new Map();
    stops.forEach((s) => {
      const el = stopEls.current.get(s.id);
      if (el) snapshots.set(s.id, el.getBoundingClientRect().top);
    });

    setFormData((prev) => syncStopsIntoFormData(prev, original));

    setPending(false);
    setIsAccepted(false);
    preOptimizeStopsRef.current = null;

    runFlipAnimation(original, stopEls, snapshots);
  };

  const handleNightsChange = (stopId, delta) => {
    setFormData((prev) => {
      const prevStops = Array.isArray(prev.stops) ? prev.stops : [];
      const nextStops = prevStops.map((s) => {
        if (s.id !== stopId) return s;
        const current = parseInt(s.nights, 10) || 0;
        const next = Math.max(0, current + delta);
        return { ...s, nights: next };
      });
      const newTotal = nextStops.reduce(
        (sum, s) => sum + (parseInt(s.nights, 10) || 0),
        0,
      );
      let newEndDate = prev.endDate;
      if (prev.startDate && newTotal > 0) {
        const start = new Date(prev.startDate);
        start.setDate(start.getDate() + newTotal);
        newEndDate = start.toISOString().split("T")[0];
      }
      const synced = syncStopsIntoFormData(prev, nextStops);
      return { ...synced, endDate: newEndDate };
    });
  };

  const handleEditStart = (stopId) => setEditingStopId(stopId);
  const handleEditCancel = () => setEditingStopId(null);

  const handleEditCommit = (stopId, newLocation) => {
    setFormData((prev) => {
      const prevStops = Array.isArray(prev.stops) ? prev.stops : [];
      const nextStops = prevStops.map((s) => {
        if (s.id !== stopId) return s;
        return {
          ...locationToStop(newLocation, parseInt(s.nights, 10) || 1),
          id: s.id,
        };
      });
      return syncStopsIntoFormData(prev, nextStops);
    });
    setEditingStopId(null);
  };

  const handleDragEnd = ({ active, over }) => {
    setActiveId(null);
    if (pendingOptimized) setPending(false);
    if (isAccepted) setIsAccepted(false);
    preOptimizeStopsRef.current = null;
    if (!over || active.id === over.id) return;

    const isCountryDrag = String(active.id).startsWith("country::");
    const isOverCountry = String(over.id).startsWith("country::");

    if (isCountryDrag && isOverCountry) {
      const fromKey = String(active.id).replace("country::", "");
      const toKey = String(over.id).replace("country::", "");
      const fromIdx = groups.findIndex((g) => g.key === fromKey);
      const toIdx = groups.findIndex((g) => g.key === toKey);
      if (fromIdx === -1 || toIdx === -1) return;
      const newGroups = arrayMove(groups, fromIdx, toIdx);
      const nextStops = newGroups.flatMap((g) => g.items);
      setFormData((prev) => syncStopsIntoFormData(prev, nextStops));
      return;
    }

    if (!isCountryDrag && !isOverCountry) {
      const fromId = String(active.id).replace("stop::", "");
      const toId = String(over.id).replace("stop::", "");
      const fromGroup = groups.find((g) => g.items.some((s) => s.id === fromId));
      const toGroup = groups.find((g) => g.items.some((s) => s.id === toId));
      if (!fromGroup || !toGroup || fromGroup.key !== toGroup.key) return;
      const fromIdx = stops.findIndex((s) => s.id === fromId);
      const toIdx = stops.findIndex((s) => s.id === toId);
      if (fromIdx === -1 || toIdx === -1) return;
      const nextStops = arrayMove(stops, fromIdx, toIdx);
      setFormData((prev) => syncStopsIntoFormData(prev, nextStops));
    }
  };

  const activeStopId =
    activeId && !String(activeId).startsWith("country::")
      ? String(activeId).replace("stop::", "")
      : null;
  const activeStop = activeStopId
    ? stops.find((s) => s.id === activeStopId)
    : null;
  const activeGrpKey =
    activeId && String(activeId).startsWith("country::")
      ? String(activeId).replace("country::", "")
      : null;
  const activeGroup = activeGrpKey
    ? groups.find((g) => g.key === activeGrpKey)
    : null;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={({ active }) => setActiveId(active.id)}
      onDragEnd={handleDragEnd}
    >
      <div className="mt-3 rounded-2xl border border-gray-200 bg-white overflow-hidden shadow-sm">
        {bannerErrors.length > 0 && (
          <div className="px-4 py-2.5 bg-red-50 border-b border-red-200">
            <ul className="space-y-1">
              {bannerErrors.map((err, i) => (
                <li key={`${err.id}_${i}`} className="text-xs text-red-700 flex items-start gap-1.5">
                  <span className="text-red-500 mt-0.5">●</span>
                  <span>{err.message}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100 bg-gray-50/50">
          <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
            Drag to reorder
          </span>
          <button
            type="button"
            onClick={optimizeRoute}
            disabled={isOptimizing || pendingOptimized || isAccepted}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200
              ${isAccepted
                ? "bg-teal-50 text-teal-600 border border-teal-200 cursor-not-allowed"
                : pendingOptimized || isOptimizing
                  ? "bg-gray-50 text-gray-400 border border-gray-200 cursor-not-allowed opacity-60"
                  : "bg-white text-teal-600 border border-teal-400 shadow-[0_0_12px_rgba(20,184,166,0.4)] hover:shadow-[0_0_16px_rgba(20,184,166,0.6)] hover:bg-teal-50 hover:-translate-y-0.5 transition-all cursor-pointer"
              }
            `}
          >
            {isOptimizing ? (
              <>
                <div className="w-3 h-3 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
                Optimizing...
              </>
            ) : isAccepted ? (
              <>
                <CheckIcon />
                Route Optimized
              </>
            ) : (
              <>
                <RouteIcon />
                Optimize Route
              </>
            )}
          </button>
        </div>

        <SortableContext
          items={countryIds}
          strategy={verticalListSortingStrategy}
        >
          {groups.map((group, groupIdx) => (
            <SortableCountrySection
              key={group.key}
              groupKey={group.key}
              country={group.country}
              countryCode={group.countryCode}
              items={group.items}
              groupIdx={groupIdx}
              enrichedById={enrichedById}
              enrichedStops={enrichedStops}
              origin={origin}
              onNightsChange={handleNightsChange}
              editingStopId={editingStopId}
              onEditStart={handleEditStart}
              onEditCommit={handleEditCommit}
              onEditCancel={handleEditCancel}
              onStopMount={handleStopMount}
            />
          ))}
        </SortableContext>

        <div
          className={`overflow-hidden transition-all duration-300 ease-out ${pendingOptimized ? "max-h-12 opacity-100" : "max-h-0 opacity-0"
            }`}
          aria-live="polite"
        >
          <div className="px-4 py-2 bg-teal-50 border-t border-teal-100 flex items-center gap-2.5">
            <p className="flex-1 text-xs font-medium text-teal-800 text-center min-w-0">
              Route optimized — keep this order?
            </p>

            <button
              type="button"
              onClick={handleAcceptOptimize}
              aria-label="Accept optimized route"
              className="w-7 h-7 rounded-full bg-emerald-500 flex items-center justify-center text-white shrink-0 hover:bg-emerald-600 transition-colors duration-150 cursor-pointer active:scale-95"
            >
              <ConfirmCheckIcon />
            </button>

            <button
              type="button"
              onClick={handleRevertOptimize}
              aria-label="Revert to original route order"
              className="w-7 h-7 rounded-full bg-red-500 flex items-center justify-center text-white shrink-0 hover:bg-red-600 transition-colors duration-150 cursor-pointer active:scale-95"
            >
              <ConfirmXIcon />
            </button>
          </div>
        </div>

        <div className="px-4 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
          <div className="flex flex-col gap-0.5 min-w-0">
            <div
              className={`flex items-center gap-1.5 text-sm font-medium ${totalAssignedNights === numberOfNights
                ? "text-teal-600"
                : totalAssignedNights > numberOfNights
                  ? "text-red-500"
                  : "text-amber-500"
                }`}
            >
              {totalAssignedNights === numberOfNights && <CheckIcon />}
              <span>
                {totalAssignedNights} / {numberOfNights} nights assigned
              </span>
              {totalAssignedNights > numberOfNights && (
                <span className="text-xs font-normal text-red-400">
                  — reduce to continue
                </span>
              )}
              {totalAssignedNights < numberOfNights && nightsLeft > 0 && (
                <span className="text-xs font-normal text-amber-400">
                  — {nightsLeft} remaining
                </span>
              )}
            </div>
            {numberOfNights > 0 && (
              <p className="text-xs text-gray-500">
                {totalAssignedNights} {totalAssignedNights === 1 ? "night" : "nights"} ·{" "}
                {totalDays} {totalDays === 1 ? "day" : "days"}
                {dayTripCount > 0 && (
                  <>
                    {" · "}
                    {dayTripCount} day trip
                    {dayTripCount > 1 ? "s" : ""} included
                  </>
                )}
              </p>
            )}
            {validation.warnings.length > 0 && (
              <ul className="mt-1 space-y-0.5">
                {validation.warnings.map((w, i) => (
                  <li
                    key={`${w.id}_${i}`}
                    className="text-[11px] text-amber-700 leading-snug flex items-start gap-1"
                  >
                    <span className="text-amber-500 mt-0.5">⚠</span>
                    <span>{w.message}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="flex gap-1">
            {Array.from({ length: Math.min(numberOfNights, 16) }).map(
              (_, i) => (
                <div
                  key={i}
                  className={`h-1.5 w-2 rounded-full transition-all duration-200 ${i < totalAssignedNights
                    ? totalAssignedNights > numberOfNights
                      ? "bg-red-400"
                      : "bg-teal-400"
                    : "bg-gray-200"
                    }`}
                />
              ),
            )}
          </div>
        </div>
      </div>

      <DragOverlay
        dropAnimation={{
          duration: 180,
          easing: "cubic-bezier(0.18, 0.67, 0.6, 1.22)",
        }}
      >
        {activeStop ? (
          <StopOverlayCard stop={activeStop} />
        ) : activeGroup ? (
          <CountryOverlayCard
            country={activeGroup.country}
            countryCode={activeGroup.countryCode}
            itemCount={activeGroup.items.length}
            groupNights={activeGroup.items.reduce(
              (s, stop) => s + (parseInt(stop.nights, 10) || 0),
              0,
            )}
          />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};

export default CustomStaySection;
